import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";

const easeElegant = [0.76, 0, 0.24, 1];

export const Contacts = () => {
  const { t } = useLang();
  const companyData = t("contact.companyData") || [];

  return (
    <section
      id="contact"
      className="relative bg-[#0A0A0A] py-28 md:py-40 border-t border-white/10"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4 mb-8">
              <span className="block h-px w-12 bg-[#C5A059]" />
              <span className="overline">{t("contact.overline")}</span>
            </div>
          </div>
          <div className="md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.1, ease: easeElegant }}
              className="font-serif text-[#F4F4F0] text-5xl md:text-6xl lg:text-7xl font-light leading-[1] tracking-tight max-w-[16ch]"
            >
              {t("contact.title")}
            </motion.h2>
            <p className="mt-8 text-[#A1A1AA] text-base md:text-lg leading-[1.65] font-light max-w-[54ch]">
              {t("contact.lede")}
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-8 md:gap-12">
          {/* Direct contacts */}
          <div className="md:col-span-5 border-t border-white/10 pt-10">
            <span className="overline !text-[#52525B]">Direct</span>
            <div className="mt-8 space-y-8">
              <a
                href="mailto:info@felicainvest.com"
                className="flex items-start gap-6 group"
                data-testid="contact-email"
              >
                <Mail size={18} className="text-[#C5A059] mt-1 flex-shrink-0" strokeWidth={1.4} />
                <div>
                  <span className="overline !text-[#52525B] block">{t("contact.emailLabel")}</span>
                  <div className="mt-2 font-serif text-[#F4F4F0] text-2xl md:text-3xl font-light group-hover:text-[#C5A059] transition-colors">
                    {t("contact.email")}
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-6" data-testid="contact-phone">
                <Phone size={18} className="text-[#C5A059] mt-1 flex-shrink-0" strokeWidth={1.4} />
                <div>
                  <span className="overline !text-[#52525B] block">{t("contact.phoneLabel")}</span>
                  <div className="mt-2 font-serif text-[#F4F4F0] text-2xl md:text-3xl font-light">
                    {t("contact.phone")}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-6" data-testid="contact-address">
                <MapPin size={18} className="text-[#C5A059] mt-1 flex-shrink-0" strokeWidth={1.4} />
                <div>
                  <span className="overline !text-[#52525B] block">{t("contact.addressLabel")}</span>
                  <div className="mt-2 font-serif text-[#F4F4F0] text-2xl md:text-3xl font-light">
                    {t("contact.address")}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-14">
              <a href="mailto:info@felicainvest.com" className="btn-gold" data-testid="contact-cta">
                {t("contact.cta")}
                <ArrowUpRight size={16} strokeWidth={1.4} />
              </a>
            </div>
          </div>

          {/* Company data */}
          <div className="md:col-span-7 border-t border-white/10 pt-10" data-testid="company-data">
            <div className="flex items-center justify-between mb-8">
              <span className="overline !text-[#52525B]">{t("contact.companyBlock")}</span>
              <span className="font-mono-ish text-[10px] tracking-[0.3em] uppercase text-[#52525B]">
                § / Legal
              </span>
            </div>
            <dl>
              {companyData.map((row, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-5% 0px" }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: easeElegant }}
                  className="grid grid-cols-12 gap-4 py-5 border-b border-white/10"
                >
                  <dt className="col-span-5 md:col-span-4 font-sans text-[11px] tracking-[0.24em] uppercase text-[#A1A1AA]">
                    {row.k}
                  </dt>
                  <dd className="col-span-7 md:col-span-8 font-serif text-[#F4F4F0] text-lg md:text-xl font-light">
                    {row.v}
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};
